/**
 * js/altech-import.js — window.AltechImport, the ONE import door.
 *
 * Three ways into the same pipeline: the step-0 drop zone, a persistent
 * dashboard tile (js/import-drop-card.js) and the Cmd+K command. Before this
 * module the only production caller of the router was the step-0 drop zone, so
 * every other way an agent could arrive holding a dec page ended in "open the
 * quoter, scroll to step 0, find the collapsed card".
 *
 * IT IS A DOOR, NOT A SECOND PIPELINE. Everything ends at
 * App._routeDroppedFiles (js/hawksoft-import.js): HawkSoft printout, then
 * EZLynx XML, then the AI scan. What this file adds is the two guards the bare
 * router cannot carry:
 *
 *  1. THE TOOL HAS TO BE ON SCREEN FIRST. App._routeDroppedFiles exists from
 *     boot (eager Object.assign) but App.init() — and with it the quoting
 *     plugin's HTML — only runs on the first navigateTo('quoting'). Fired from
 *     the dashboard with that HTML never injected, App._applyMerged takes its
 *     ghost-field branch (the `!el` case) for EVERY id: the whole merge lands
 *     headlessly in App.data, counts itself and toasts a success, while
 *     handleType() finds no qType radio and the carrier carve-out never runs.
 *     That is the exact hazard CLAUDE.md records for the Client Intake Link,
 *     and the fix is the same seam: bring the tool up, then re-check AFTER the
 *     await (navigateTo's _navSeq guard can stand a superseded call down). What
 *     is re-checked is the intake MARKUP - see intakeIsUp().
 *
 *  2. AN EMPTY LIST IS NOT A REQUEST TO IMPORT. App._routeDroppedFiles([])
 *     used to reach importHawkSoftFiles(), whose no-arguments contract is
 *     "open the picker" — so a stray empty drop opened the HawkSoft file
 *     chooser and then started an empty scan. open() refuses an empty list;
 *     asking for a chooser is pick(), a different verb.
 *
 * Dependency-free and read at CALL time by every consumer, so a failed load
 * degrades to the old behaviour rather than breaking a render.
 */
window.AltechImport = (function () {
    'use strict';

    const TOOL_KEY = 'quoting';
    const CONTAINER_ID = 'quotingTool';
    const INPUT_ID = 'altechImportInput';
    const READY_MARKER = 'input[name="qType"]';   // see intakeIsUp()

    // Everything App._routeDroppedFiles can actually do something with. The
    // door owns its own chooser rather than borrowing #policyScanInput, whose
    // accept list is images and PDFs only (pinned, twice) and would hide the
    // XML half of what this door advertises. It also has to work with the
    // quoting plugin's HTML not yet injected, which #policyScanInput does not.
    const ACCEPT = 'image/*,application/pdf,.xml,text/xml,.eml,.msg,.txt,message/rfc822';

    function app() {
        return typeof window !== 'undefined' ? window.App : null;
    }

    function toast(msg, opts) {
        const A = app();
        if (A && typeof A.toast === 'function') A.toast(msg, opts);
    }

    function esc(s) {
        if (window.Utils && typeof window.Utils.escapeHTML === 'function') return window.Utils.escapeHTML(s);
        return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
            { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
        ));
    }

    function toList(files) {
        if (!files) return [];
        if (Array.isArray(files)) return files.filter(Boolean);
        if (typeof files.length !== 'number') return [];
        return Array.prototype.slice.call(files).filter(Boolean);
    }

    /**
     * Pure. Does this drop look like something the router can read at all?
     *
     * The window-level door (js/data-backup.js) fires on a drop anywhere in the
     * app, so it has to be able to say "that is not an import" without starting
     * a 60 second quota-burning AI scan to find out. Deliberately generous on
     * images: a phone photo of a dec page is the single most common import.
     */
    function looksImportable(files) {
        return toList(files).some((f) => {
            const name = String((f && f.name) || '').toLowerCase();
            const type = String((f && f.type) || '').toLowerCase();
            if (type.startsWith('image/')) return true;
            if (type === 'application/pdf' || /\.pdf$/.test(name)) return true;
            // The XML type test is anchored, not a substring: a .docx reports
            // application/vnd.openxmlformats-officedocument…, which contains
            // "xml" and would otherwise be handed to the EZLynx reader.
            if (/^(text|application)\/xml$/.test(type) || /\.xml$/.test(name)) return true;
            // A saved email (.eml, Outlook .msg) or a text note: the router
            // reads its text and any PDF attached to it.
            if (/\.(eml|msg|txt)$/.test(name)) return true;
            if (/^(message\/rfc822|application\/vnd\.ms-outlook|text\/plain)$/.test(type)) return true;
            return /\.(png|jpe?g|gif|webp|heic|heif|bmp|tiff?)$/.test(name);
        });
    }

    /**
     * Is the intake really on screen? Exported: the ONE definition every door
     * reads (js/call-hub-panels.js calls it), because two copies of a data-loss
     * guard is how they drift.
     *
     * `.active` alone is NOT the answer. navigateTo adds the class on BOTH of
     * its failure paths, each of which first overwrites the container with a
     * Retry box and then skips App.init() - so the class says yes, the markup
     * is gone, App.data is still `{}` (App.load() runs only from App.init()),
     * and the merge lands through _applyMerged's ghost-field branch and SAVES
     * over the agent's stored intake. dataset.loaded stays 'true', so that
     * screen is sticky for the session. Prove the form is there instead: the
     * qType radios are what handleType() reads and are a sibling of the step
     * divs, so its delete loop can never take them.
     */
    function intakeIsUp() {
        const el = typeof document !== 'undefined' ? document.getElementById(CONTAINER_ID) : null;
        if (!el || !el.classList || !el.classList.contains('active')) return false;
        return !!(el.querySelector && el.querySelector(READY_MARKER));
    }

    /** Bring the Personal Intake up, and answer honestly about whether it came. */
    async function ensureIntakeOnScreen() {
        if (intakeIsUp()) return true;
        const A = app();
        if (!A || typeof A.navigateTo !== 'function') return false;
        try { await A.navigateTo(TOOL_KEY); } catch (_) { return false; }
        // After the await, never before: a superseded navigateTo ends in
        // App._standDown(tool) and leaves the container inactive.
        return intakeIsUp();
    }

    /**
     * Hand a list of files to the router, with the intake on screen.
     *
     * @returns {Promise<boolean>} true only when the router was actually run.
     */
    async function open(files) {
        const list = toList(files);
        if (!list.length) return false;          // never a request to open a picker
        const A = app();
        if (!A || typeof A._routeDroppedFiles !== 'function') {
            toast('Importing is not available right now.', { type: 'error' });
            return false;
        }
        if (!looksImportable(list)) {
            toast('That file is not something the intake can read. Drop a PDF, a photo, a saved email (.eml or .msg) or an EZLynx XML file.', { type: 'error', duration: 4000 });
            return false;
        }
        const up = await ensureIntakeOnScreen();
        if (!up) {
            toast('Could not open the Personal Intake, so nothing was imported.', { type: 'error', duration: 4000 });
            return false;
        }
        await A._routeDroppedFiles(list);
        return true;
    }

    /**
     * The door's own file chooser. One hidden input on <body>, so it works from
     * the dashboard and from the palette with no plugin HTML loaded.
     */
    function pick() {
        if (typeof document === 'undefined') return false;
        let input = document.getElementById(INPUT_ID);
        if (!input) {
            input = document.createElement('input');
            input.type = 'file';
            input.id = INPUT_ID;
            input.multiple = true;
            input.accept = ACCEPT;
            input.className = 'hidden';
            input.setAttribute('aria-hidden', 'true');
            input.addEventListener('change', (e) => {
                const picked = e.target && e.target.files;
                if (picked && picked.length) void open(picked);
            });
            document.body.appendChild(input);
        }
        // Reset BEFORE the click, or picking the same file after a failed
        // import fires no change event and the retry silently does nothing.
        input.value = '';
        input.click();
        return true;
    }

    /**
     * A drop that landed nowhere in particular.
     *
     * js/data-backup.js's window listener sees every drop in the app that no
     * element claimed. Starting an import off one of those without saying so
     * would mean a file dragged onto the Reminders list quietly began a 60
     * second AI scan and then offered to overwrite the open client. So this
     * one asks first, and names the file it is asking about.
     */
    function confirmStrayDrop(files) {
        const list = toList(files);
        if (!list.length || !looksImportable(list)) return Promise.resolve(false);
        return _promptStrayDrop(list).then((ok) => (ok ? open(list) : false));
    }

    function _promptStrayDrop(list) {
        if (typeof document === 'undefined') return Promise.resolve(false);
        return new Promise((resolve) => {
            const first = String((list[0] && list[0].name) || 'this file');
            const extra = list.length > 1 ? ` and ${list.length - 1} other file${list.length > 2 ? 's' : ''}` : '';
            const modal = document.createElement('div');
            modal.className = 'modal-overlay active';
            const close = (ok) => { modal.remove(); resolve(ok); };
            modal.onclick = (e) => { if (e.target === modal) close(false); };
            modal.innerHTML = `
                <div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="ai-drop-title">
                    <div class="modal-header">
                        <span class="modal-icon">📄</span>
                        <h2 class="modal-title" id="ai-drop-title">Import this into the Personal Intake?</h2>
                    </div>
                    <div class="modal-body">
                        <p>You dropped <strong>${esc(first)}</strong>${esc(extra)} outside an import area.</p>
                        <p style="margin-top:8px;color:var(--text-secondary);font-size:13px;">
                            Importing opens the Personal Intake and reads the document. A dec
                            page or a photo is sent to the scanner, which can take up to a
                            minute and uses part of the daily scan allowance.
                        </p>
                    </div>
                    <div class="modal-actions">
                        <button class="modal-btn modal-btn-secondary" id="ai-drop-no">Cancel</button>
                        <button class="modal-btn modal-btn-primary" id="ai-drop-yes">Import it</button>
                    </div>
                </div>`;
            document.body.appendChild(modal);
            modal.querySelector('#ai-drop-no').onclick = () => close(false);
            modal.querySelector('#ai-drop-yes').onclick = () => close(true);
        });
    }

    return {
        open,
        pick,
        looksImportable,
        confirmStrayDrop,
        ensureIntakeOnScreen,
        intakeIsUp,
        ACCEPT, TOOL_KEY, CONTAINER_ID, INPUT_ID, READY_MARKER,
    };
})();
